const SEARCH_DEBOUNCE_MS = 250

export default class DeviceScenesTool {
  static get toolbox() {
    return {
      title: "Device",
      icon: "<span class=\"icon-[lucide--smartphone] size-4\"></span>"
    }
  }

  static get sanitize() {
    return {
      deviceId: false,
      deviceName: false
    }
  }

  constructor({ data, readOnly }) {
    this.readOnly = Boolean(readOnly)
    this.data = {
      deviceId: data?.deviceId || null,
      deviceName: data?.deviceName || ""
    }
    this.wrapper = null
    this.searchInput = null
    this.resultsEl = null
    this.selectedEl = null
    this.searchTimeout = null
  }

  render() {
    this.wrapper = document.createElement("div")
    this.wrapper.className = "editorjs-device-scenes rounded-box border border-base-content/20 bg-base-200/40 p-4"

    const heading = document.createElement("h4")
    heading.className = "mb-1 text-sm font-semibold"
    heading.textContent = "Device scenes (Front/Back)"

    const help = document.createElement("p")
    help.className = "mb-3 text-xs text-base-content/70"
    help.textContent = "Search an existing device or create a new one, then embed its front/back scenes."

    this.selectedEl = document.createElement("div")
    this.selectedEl.className = "mb-3 rounded-box border border-base-content/20 bg-base-100 px-3 py-2 text-sm"

    this.searchInput = document.createElement("input")
    this.searchInput.type = "search"
    this.searchInput.placeholder = "Search devices..."
    this.searchInput.className = "input input-sm w-full"
    this.searchInput.autocomplete = "off"

    this.resultsEl = document.createElement("div")
    this.resultsEl.className = "mt-2 space-y-1"

    this.searchInput.addEventListener("input", () => this.debouncedSearch())

    this.wrapper.append(heading, help, this.selectedEl)
    if (!this.readOnly) {
      this.wrapper.append(this.searchInput, this.resultsEl)
    }

    this.renderSelectedDevice()
    return this.wrapper
  }

  save() {
    return {
      deviceId: this.data.deviceId,
      deviceName: this.data.deviceName
    }
  }

  validate(savedData) {
    return Boolean(savedData?.deviceId)
  }

  renderSelectedDevice() {
    if (!this.selectedEl) return

    if (!this.data.deviceId) {
      this.selectedEl.innerHTML = "<span class=\"text-base-content/70\">No device selected yet.</span>"
      return
    }

    const name = this.escapeHtml(this.data.deviceName || `Device #${this.data.deviceId}`)
    const clearButton = this.readOnly ? "" : "<button type=\"button\" data-clear-device class=\"btn btn-ghost btn-xs\">Clear</button>"
    this.selectedEl.innerHTML = `
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p class="text-xs uppercase tracking-wide text-base-content/60">Embedded device</p>
          <p class="font-semibold">${name}</p>
          <p class="text-xs text-base-content/60">Scenes: Front and Back</p>
        </div>
        ${clearButton}
      </div>
    `

    if (!this.readOnly) {
      this.selectedEl.querySelector("[data-clear-device]")?.addEventListener("click", () => {
        this.data.deviceId = null
        this.data.deviceName = ""
        this.renderSelectedDevice()
      })
    }
  }

  debouncedSearch() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this.searchDevices(), SEARCH_DEBOUNCE_MS)
  }

  async searchDevices() {
    if (!this.resultsEl || !this.searchInput) return
    const query = this.searchInput.value.trim()
    if (!query.length) {
      this.resultsEl.innerHTML = ""
      return
    }

    this.resultsEl.innerHTML = "<p class=\"text-xs text-base-content/60\">Searching…</p>"

    try {
      const response = await fetch(`/admin/devices/search?q=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) {
        throw new Error("Search failed")
      }

      const payload = await response.json()
      const devices = Array.isArray(payload?.devices) ? payload.devices : []
      this.renderSearchResults(devices)
    } catch {
      this.resultsEl.innerHTML = "<p class=\"text-xs text-error\">Could not search devices.</p>"
    }
  }

  renderSearchResults(devices) {
    if (!this.resultsEl) return

    if (devices.length === 0) {
      this.resultsEl.innerHTML = "<p class=\"text-xs text-base-content/60\">No matching devices.</p>"
      return
    }

    this.resultsEl.innerHTML = ""

    devices.forEach((device) => {
      const row = document.createElement("button")
      row.type = "button"
      row.className = "btn btn-ghost btn-sm w-full justify-start"
      row.textContent = device.name
      row.addEventListener("click", () => {
        this.data.deviceId = device.id
        this.data.deviceName = device.name
        this.renderSelectedDevice()
        this.resultsEl.innerHTML = ""
      })
      this.resultsEl.appendChild(row)
    })
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;")
      .replaceAll("'", "&#39;")
  }
}
